import React, { useState, useEffect } from 'react';
import {array, bool, func, string} from 'prop-types';
import {find, uniq, trim} from 'lodash';

import { makeStyles } from '@material-ui/core';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';

import Modal from '../../../Modal';
import AutocompleteCreate from '../../../AutocompleteCreate';
import FilePicker from '../../../FilePicker/FilePicker';
import { useFetchResource } from '../../../../helpers/hooks';
import { onlineHubHost } from '../../../../config';

import './BsnSetupForm.css';

const useInputStyles = makeStyles(() => ({
  root: {
    marginBottom: '15px'
  }
}));

const useSubmitButtonStyles = makeStyles(() => ({
  root: {
    marginLeft: '20px'
  }
}));

const useLoadingStyles = makeStyles(() => ({
  root: {
    position: 'absolute',
    right: '30px'
  }
}));

function mapNetworks(items) {
  return uniq(items.map(item => item.network));
}

function getAutorunFolder(autorunUrl) {
  const reg = /https:\/\/(.*?)\/(bsn\/)?(.*)\/autorun.zip/i
  return reg.exec(autorunUrl)[3];
}

function getErrorInputs(inputsByKey) {
  return Object.keys(inputsByKey).reduce((soFar, key) => !inputsByKey[key] ? soFar.concat([key]) : soFar, []);
}

export default function BsnSetupForm({itemId, items, active, duplicate = false, onClose, onSubmit}) {
  const inputClasses = useInputStyles();
  const submitButtonClasses = useSubmitButtonStyles();
  const loadingClasses = useLoadingStyles();

  const [name, setName] = useState('');
  const [network, setNetwork] = useState(null);
  const [autorunFolder, setAutorunFolder] = useState(null);
  const [autorunFolderPicker, setAutorunFolderPicker] = useState(false);
  const [autorunFolderFile, setAutorunFolderFile] = useState(null);
  const [errorInputs, setErrorInputs] = useState([]);
  const [loading, setLoading] = useState(false);

  const {data, retry} = useFetchResource(`${onlineHubHost}/app/autorunFolders`);
  const autorunFolders = data || [];

  useEffect(() => {
    if (active) {
      retry();
    } else {
      setName('');
      setNetwork('');
      setAutorunFolder('')
      setErrorInputs([]);
      setLoading(false);
      setAutorunFolderPicker(false);
      setAutorunFolderFile(null);
    }
  }, [active, retry])

  useEffect(() => {
    if (active && itemId) {
      const item = find(items, {id: itemId});
      setName(item.name);
      setNetwork(item.network);
      setAutorunFolderPicker(true);
      setAutorunFolder(getAutorunFolder(item.payload.bDeploy.url));
    }
  }, [active, itemId, items])

  function handleInputChange({target: {value, id}}) {
    if (id === 'addBsnSetup-input-name') {
      setName(value);
    }
  }

  function handleAutorunFolderChange(value) {
    setAutorunFolder(value);
    setAutorunFolderPicker(true);
  }

  function handleAutorunFileSelect(file) {
    setAutorunFolderFile(file);
  }

  function handleSubmit() {
    const errorInputs = getErrorInputs(duplicate ? {network} : {name, network, autorunFolder});
    setErrorInputs(errorInputs);
    if (!errorInputs.length) {
      setLoading(true);
      const data = duplicate ? {network} : {name, network, autorunFolder: trim(autorunFolder, '/'), autorunFolderFile};
      onSubmit(data);
    }
  }

  return (
    <Modal active={active} onClose={onClose}>
      <div className="AddBsnSetup">
        <h2 className="AddBsnSetup-header">{duplicate ? 'Duplicate BSN Setup(s)' : 'New BSN Setup'}</h2>
        {!duplicate ?
          <TextField
            id="addBsnSetup-input-name"
            classes={{
              root: inputClasses.root
            }}
            value={name}
            label="Name"
            fullWidth
            required
            error={errorInputs.includes('name')}
            onChange={handleInputChange}
          />
        : null}
        <AutocompleteCreate
          classes={{
            root: inputClasses.root
          }}
          value={network}
          error={errorInputs.includes('network')}
          label="Network"
          source={mapNetworks(items)}
          onChange={setNetwork}
        />
        <div className="AddBsnSetup-autorun">
          <AutocompleteCreate
            classes={{
              root: inputClasses.root
            }}
            value={autorunFolder}
            error={errorInputs.includes('autorunFolder')}
            label="Autorun folder"
            source={autorunFolders}
            hidden={duplicate}
            onChange={handleAutorunFolderChange}
          />
          {autorunFolderPicker && !duplicate ?
            <FilePicker
              className="AddBsnSetup-autorun-filePicker"
              accept=".zip"
              onSelect={handleAutorunFileSelect}
            />
          : null}
        </div>
        <div className="AddBsnSetup-footer">
          <Button variant="contained" disabled={loading} onClick={onClose}>cancel</Button>
          <Button
            classes={{root: submitButtonClasses.root}}
            variant="contained"
            color="primary"
            disabled={loading}
            onClick={handleSubmit}
          >
            submit
          </Button>
          {loading && <CircularProgress size={24} className={loadingClasses.root} />}
        </div>
      </div>
    </Modal>
  );
}

BsnSetupForm.propTypes = {
  itemId: string,
  items: array,
  active: bool,
  duplicate: bool,
  onSubmit: func,
  onClose: func
}
